import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { platformAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import {
  AdminEmptyState,
  AdminFilterCard,
  AdminModalShell,
  AdminPageHeader,
  AdminStatCard,
  AdminTableCard,
} from '../../components/AdminSurface';

type CompanySummary = {
  id: string;
  name: string;
};

type SupportStaffRecord = {
  id: string;
  email: string;
  accessible_company_ids: string[];
  support_write_permissions: string[];
  created_at: string;
};

type SupportWritePermissionDefinition = {
  id: string;
  label: string;
  description: string;
};

type CreateSupportStaffForm = {
  email: string;
  password: string;
  confirmPassword: string;
  accessible_company_ids: string[];
  support_write_permissions: string[];
};

const INITIAL_CREATE_FORM: CreateSupportStaffForm = {
  email: '',
  password: '',
  confirmPassword: '',
  accessible_company_ids: [],
  support_write_permissions: [],
};

function toggleSelection(values: string[], value: string): string[] {
  if (values.includes(value)) {
    return values.filter((entry) => entry !== value);
  }

  return [...values, value];
}

function PlatformSupportStaff() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateSupportStaffForm>(
    INITIAL_CREATE_FORM,
  );

  const [editingStaff, setEditingStaff] = useState<SupportStaffRecord | null>(null);
  const [editCompanies, setEditCompanies] = useState<string[]>([]);
  const [editPermissions, setEditPermissions] = useState<string[]>([]);
  const [passwordStaff, setPasswordStaff] = useState<SupportStaffRecord | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });

  const {
    data: supportStaff = [],
    isLoading: isSupportStaffLoading,
  } = useQuery('platform-support-staff', () => platformAPI.getSupportStaff(), {
    select: (response) => (response.data || []) as SupportStaffRecord[],
  });

  const {
    data: companies = [],
    isLoading: isCompaniesLoading,
  } = useQuery('platform-companies-support-staff', () => platformAPI.getCompanies(), {
    select: (response) => (response.data || []) as CompanySummary[],
  });

  const {
    data: permissionCatalog = [],
    isLoading: isPermissionCatalogLoading,
  } = useQuery(
    'platform-support-write-permissions',
    () => platformAPI.getSupportWritePermissionCatalog(),
    {
      select: (response) =>
        (response.data || []) as SupportWritePermissionDefinition[],
    },
  );

  const createMutation = useMutation(
    (payload: {
      email: string;
      password: string;
      accessible_company_ids: string[];
      support_write_permissions: string[];
    }) => platformAPI.createSupportStaff(payload),
    {
      onSuccess: () => {
        toast.success('Support staff account created');
        queryClient.invalidateQueries('platform-support-staff');
        setShowCreateModal(false);
        setCreateForm(INITIAL_CREATE_FORM);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to create support staff');
      },
    },
  );

  const updateMutation = useMutation(
    async (payload: {
      id: string;
      accessible_company_ids: string[];
      support_write_permissions: string[];
    }) => {
      await platformAPI.updateSupportStaffCompanies(payload.id, {
        accessible_company_ids: payload.accessible_company_ids,
      });
      await platformAPI.updateSupportStaffWritePermissions(payload.id, {
        support_write_permissions: payload.support_write_permissions,
      });
    },
    {
      onSuccess: () => {
        toast.success('Support staff access updated');
        queryClient.invalidateQueries('platform-support-staff');
        setEditingStaff(null);
        setEditCompanies([]);
        setEditPermissions([]);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to update support staff access');
      },
    },
  );

  const removeMutation = useMutation(
    (id: string) => platformAPI.removeSupportStaff(id),
    {
      onSuccess: () => {
        toast.success('Support staff account removed');
        queryClient.invalidateQueries('platform-support-staff');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to remove support staff');
      },
    },
  );

  const setPasswordMutation = useMutation(
    (payload: { id: string; password: string; confirmPassword: string }) =>
      platformAPI.setSupportStaffPassword(payload.id, {
        password: payload.password,
        confirmPassword: payload.confirmPassword,
      }),
    {
      onSuccess: () => {
        toast.success('Support staff password updated');
        setPasswordStaff(null);
        setPasswordForm({ password: '', confirmPassword: '' });
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to update support staff password');
      },
    },
  );

  const companyNameMap = useMemo(() => {
    return new Map(companies.map((company) => [company.id, company.name]));
  }, [companies]);

  const permissionLabelMap = useMemo(() => {
    return new Map(permissionCatalog.map((permission) => [permission.id, permission.label]));
  }, [permissionCatalog]);

  const filteredSupportStaff = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return supportStaff;
    }

    return supportStaff.filter((staff) =>
      staff.email.toLowerCase().includes(normalizedSearch),
    );
  }, [search, supportStaff]);

  const totalAssignments = supportStaff.reduce(
    (sum, staff) => sum + (staff.accessible_company_ids || []).length,
    0,
  );

  const totalWriteGrants = supportStaff.reduce(
    (sum, staff) => sum + (staff.support_write_permissions || []).length,
    0,
  );

  const openEditModal = (staff: SupportStaffRecord) => {
    setEditingStaff(staff);
    setEditCompanies([...(staff.accessible_company_ids || [])]);
    setEditPermissions([...(staff.support_write_permissions || [])]);
  };

  const handleCreate = () => {
    if (!createForm.email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!createForm.password) {
      toast.error('Password is required');
      return;
    }

    if (createForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (createForm.accessible_company_ids.length === 0) {
      toast.error('Assign at least one company');
      return;
    }

    createMutation.mutate({
      email: createForm.email.trim(),
      password: createForm.password,
      accessible_company_ids: createForm.accessible_company_ids,
      support_write_permissions: createForm.support_write_permissions,
    });
  };

  const handleUpdate = () => {
    if (!editingStaff) return;

    if (editCompanies.length === 0) {
      toast.error('Assign at least one company');
      return;
    }

    updateMutation.mutate({
      id: editingStaff.id,
      accessible_company_ids: editCompanies,
      support_write_permissions: editPermissions,
    });
  };

  const handleRemove = (staff: SupportStaffRecord) => {
    const confirmed = window.confirm(
      `Remove support staff account ${staff.email}? This cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    removeMutation.mutate(staff.id);
  };

  const openPasswordModal = (staff: SupportStaffRecord) => {
    setPasswordStaff(staff);
    setPasswordForm({ password: '', confirmPassword: '' });
  };

  const handleSetPassword = () => {
    if (!passwordStaff) return;

    if (!passwordForm.password) {
      toast.error('Password is required');
      return;
    }

    if (passwordForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (passwordForm.password !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setPasswordMutation.mutate({
      id: passwordStaff.id,
      password: passwordForm.password,
      confirmPassword: passwordForm.confirmPassword,
    });
  };

  if (isSupportStaffLoading || isCompaniesLoading || isPermissionCatalogLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title="Support Staff Access"
          description="Manage assigned-company scope and explicit write permissions for support staff from a shared operations surface."
          eyebrow="Platform administration"
          actions={(
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create Support Staff</span>
            </button>
          )}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <AdminStatCard
            icon={<UserGroupIcon className="h-8 w-8" />}
            label="Support Staff"
            value={supportStaff.length}
            tone="info"
          />
          <AdminStatCard
            icon={<BuildingOfficeIcon className="h-8 w-8" />}
            label="Company Assignments"
            value={totalAssignments}
            tone="success"
          />
          <AdminStatCard
            icon={<KeyIcon className="h-8 w-8" />}
            label="Write Permission Grants"
            value={totalWriteGrants}
            tone="warning"
          />
        </div>

        <AdminFilterCard
          title="Search"
          description="Filter the directory by support-staff email address."
        >
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-on-surface-variant" />
            <input
              type="text"
              placeholder="Search support staff by email..."
              className="input-field pl-10"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </AdminFilterCard>

        <AdminTableCard
          title="Support directory"
          description="See staff identity, assigned company scope, and explicit write privileges in one table."
          meta={`${filteredSupportStaff.length} of ${supportStaff.length} staff shown`}
        >
          <table className="admin-table-grid min-w-full">
            <thead className="admin-table-head">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Support Staff
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Company Access
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Write Permissions
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Created
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                Actions
              </th>
            </tr>
            </thead>
            <tbody className="admin-table-body">
            {filteredSupportStaff.map((staff) => (
              <tr key={staff.id} className="admin-table-row">
                <td className="px-4 py-4 align-top">
                  <div className="font-medium text-on-surface">{staff.email}</div>
                  <div className="text-xs text-on-surface-variant">{staff.id}</div>
                </td>
                <td className="px-4 py-4 align-top">
                  <div className="flex flex-wrap gap-1.5">
                    {(staff.accessible_company_ids || []).map((companyId) => (
                      <span
                        key={`${staff.id}-company-${companyId}`}
                        className="admin-soft-pill inline-flex rounded-full border border-outline/70 bg-success-container px-2.5 py-1 text-xs font-medium text-on-success-container"
                      >
                        {companyNameMap.get(companyId) || companyId}
                      </span>
                    ))}
                    {(staff.accessible_company_ids || []).length === 0 && (
                      <span className="admin-soft-pill inline-flex rounded-full border border-outline/70 bg-error-container px-2.5 py-1 text-xs font-medium text-on-error-container">
                        Unassigned
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 align-top">
                  <div className="flex flex-wrap gap-1.5">
                    {(staff.support_write_permissions || []).map((permissionId) => (
                      <span
                        key={`${staff.id}-permission-${permissionId}`}
                        className="admin-soft-pill inline-flex rounded-full border border-outline/70 bg-warning-container px-2.5 py-1 text-xs font-medium text-on-warning-container"
                      >
                        {permissionLabelMap.get(permissionId) || permissionId}
                      </span>
                    ))}
                    {(staff.support_write_permissions || []).length === 0 && (
                      <span className="admin-soft-pill inline-flex rounded-full border border-outline/70 bg-surface-container-low px-2.5 py-1 text-xs font-medium text-on-surface">
                        Read-only
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 align-top text-sm text-on-surface-variant">
                  {staff.created_at
                    ? new Date(staff.created_at).toLocaleString()
                    : 'N/A'}
                </td>
                <td className="px-4 py-4 align-top">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openPasswordModal(staff)}
                      className="admin-action-pill inline-flex items-center gap-1 rounded-md bg-warning-container px-3 py-1.5 text-sm font-medium text-on-warning-container hover:brightness-110"
                    >
                      <KeyIcon className="h-4 w-4" />
                      Set Password
                    </button>
                    <button
                      onClick={() => openEditModal(staff)}
                      className="admin-action-pill inline-flex items-center gap-1 rounded-md bg-info-container px-3 py-1.5 text-sm font-medium text-on-info-container hover:brightness-110"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                      Edit Access
                    </button>
                    <button
                      onClick={() => handleRemove(staff)}
                      className="admin-action-pill inline-flex items-center gap-1 rounded-md bg-error-container px-3 py-1.5 text-sm font-medium text-on-error-container hover:brightness-110"
                      disabled={removeMutation.isLoading}
                    >
                      <TrashIcon className="h-4 w-4" />
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            </tbody>
          </table>

          {filteredSupportStaff.length === 0 && (
            <AdminEmptyState
              icon={<ShieldCheckIcon className="h-6 w-6" />}
              title="No support staff found"
              description={search ? 'Try a different search term.' : 'Create your first support staff account.'}
            />
          )}
        </AdminTableCard>
      </div>

      {showCreateModal && (
        <AdminModalShell
          title="Create Support Staff"
          description="Provision a support account with assigned-company scope and optional write permissions."
          onClose={() => {
            setShowCreateModal(false);
            setCreateForm(INITIAL_CREATE_FORM);
          }}
          maxWidthClassName="max-w-2xl"
          footer={(
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateForm(INITIAL_CREATE_FORM);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isLoading}
                className="btn-primary"
              >
                {createMutation.isLoading ? 'Creating...' : 'Create Support Staff'}
              </button>
            </div>
          )}
        >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-on-surface-variant">Email</label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="support@example.com"
                    value={createForm.email}
                    onChange={(event) =>
                      setCreateForm({ ...createForm, email: event.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-on-surface-variant">Password</label>
                  <input
                    type="password"
                    className="input-field"
                    placeholder="Minimum 8 characters"
                    value={createForm.password}
                    onChange={(event) =>
                      setCreateForm({ ...createForm, password: event.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Confirm Password</label>
                <input
                  type="password"
                  className="input-field"
                  placeholder="Re-enter password"
                  value={createForm.confirmPassword}
                  onChange={(event) =>
                    setCreateForm({
                      ...createForm,
                      confirmPassword: event.target.value,
                    })
                  }
                />
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-on-surface-variant">
                  Assigned Companies (required)
                </p>
                <div className="admin-subpanel max-h-40 space-y-2 overflow-y-auto rounded-xl p-3">
                  {companies.map((company) => (
                    <label
                      key={company.id}
                      className="flex items-start gap-2 text-sm text-on-surface"
                    >
                      <input
                        type="checkbox"
                        checked={createForm.accessible_company_ids.includes(company.id)}
                        onChange={() =>
                          setCreateForm({
                            ...createForm,
                            accessible_company_ids: toggleSelection(
                              createForm.accessible_company_ids,
                              company.id,
                            ),
                          })
                        }
                      />
                      <span>{company.name}</span>
                    </label>
                  ))}
                  {companies.length === 0 && (
                    <p className="text-sm text-on-surface-variant">No companies are available yet.</p>
                  )}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-on-surface-variant">
                  Write Permissions (optional)
                </p>
                <div className="admin-subpanel max-h-56 space-y-2 overflow-y-auto rounded-xl p-3">
                  {permissionCatalog.map((permission) => (
                    <label
                      key={permission.id}
                      className="flex items-start gap-2 text-sm text-on-surface"
                    >
                      <input
                        type="checkbox"
                        checked={createForm.support_write_permissions.includes(permission.id)}
                        onChange={() =>
                          setCreateForm({
                            ...createForm,
                            support_write_permissions: toggleSelection(
                              createForm.support_write_permissions,
                              permission.id,
                            ),
                          })
                        }
                      />
                      <span>
                        <span className="block font-medium">{permission.label}</span>
                        <span className="block text-xs text-on-surface-variant">{permission.description}</span>
                      </span>
                    </label>
                  ))}
                  {permissionCatalog.length === 0 && (
                    <p className="text-sm text-on-surface-variant">No write permissions are currently available.</p>
                  )}
                </div>
              </div>
            </div>
        </AdminModalShell>
      )}

      {editingStaff && (
        <AdminModalShell
          title={`Edit Access for ${editingStaff.email}`}
          description="Adjust company assignments and explicit write permissions for this support account."
          onClose={() => {
            setEditingStaff(null);
            setEditCompanies([]);
            setEditPermissions([]);
          }}
          maxWidthClassName="max-w-2xl"
          footer={(
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setEditingStaff(null);
                  setEditCompanies([]);
                  setEditPermissions([]);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updateMutation.isLoading}
                className="btn-primary"
              >
                {updateMutation.isLoading ? 'Saving...' : 'Save Access'}
              </button>
            </div>
          )}
        >
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-on-surface-variant">Assigned Companies (required)</p>
                <div className="admin-subpanel max-h-40 space-y-2 overflow-y-auto rounded-xl p-3">
                  {companies.map((company) => (
                    <label
                      key={`edit-company-${company.id}`}
                      className="flex items-start gap-2 text-sm text-on-surface"
                    >
                      <input
                        type="checkbox"
                        checked={editCompanies.includes(company.id)}
                        onChange={() =>
                          setEditCompanies(toggleSelection(editCompanies, company.id))
                        }
                      />
                      <span>{company.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-on-surface-variant">Write Permissions (optional)</p>
                <div className="admin-subpanel max-h-56 space-y-2 overflow-y-auto rounded-xl p-3">
                  {permissionCatalog.map((permission) => (
                    <label
                      key={`edit-permission-${permission.id}`}
                      className="flex items-start gap-2 text-sm text-on-surface"
                    >
                      <input
                        type="checkbox"
                        checked={editPermissions.includes(permission.id)}
                        onChange={() =>
                          setEditPermissions(toggleSelection(editPermissions, permission.id))
                        }
                      />
                      <span>
                        <span className="block font-medium">{permission.label}</span>
                        <span className="block text-xs text-on-surface-variant">{permission.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
        </AdminModalShell>
      )}

      {passwordStaff && (
        <AdminModalShell
          title={`Set Password for ${passwordStaff.email}`}
          description="Update credentials directly for this support account."
          onClose={() => {
            setPasswordStaff(null);
            setPasswordForm({ password: '', confirmPassword: '' });
          }}
          maxWidthClassName="max-w-md"
          footer={(
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setPasswordStaff(null);
                  setPasswordForm({ password: '', confirmPassword: '' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSetPassword}
                disabled={setPasswordMutation.isLoading}
                className="btn-primary"
              >
                {setPasswordMutation.isLoading ? 'Saving...' : 'Set Password'}
              </button>
            </div>
          )}
        >
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">New Password</label>
                <input
                  type="password"
                  className="input-field"
                  placeholder="Minimum 8 characters"
                  value={passwordForm.password}
                  onChange={(event) =>
                    setPasswordForm({ ...passwordForm, password: event.target.value })
                  }
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-on-surface-variant">Confirm Password</label>
                <input
                  type="password"
                  className="input-field"
                  placeholder="Re-enter new password"
                  value={passwordForm.confirmPassword}
                  onChange={(event) =>
                    setPasswordForm({
                      ...passwordForm,
                      confirmPassword: event.target.value,
                    })
                  }
                />
              </div>
            </div>
        </AdminModalShell>
      )}
    </PageLayout>
  );
}

export default PlatformSupportStaff;
